import { Link } from 'react-router-dom'
import Dotdotdot from 'react-dotdotdot'
import {
  BlurImage,
  Container,
  ImageContainer,
  CardImage,
  CoinInfoTag,
  CoinLogo,
  CoinTagDetails,
  CoinName,
  CoinTicker,
  ChainIcon,
  ChainIconWrapper,
  StatWrapper,
  StatName,
  StatValue,
  OpeningLabel,
} from '../cards/project-cards'
import useCountdownTimer from '../../../../hooks/useCountdownTimer'
import useShouldAnimate from '../../../../hooks/useShouldAnimate'
import removeMd from 'remove-markdown'
import { IPool } from '../../../../types/reducerTypes'
import LiveLoaderCircle from '../LiveLoaderCircle'
import { useEffect, useState } from 'react'
import { DescriptionText } from '../../../../shared_components/Pools/PoolCarousel/styles'
import { Box } from '../../../../shared_components/UI/Box'

export interface CardProps {
  timeLeft: string
  cardImageSrc: string
  coinName: string
  coinTicker: string
  chainIcon: string
  logoUrl?: string
  descriptionText: string
  stats: Stat[]
  address: string
  cmsData?: IPool
  isTimer?: boolean
  slug?: string
  isHardcoded?: boolean
}

type Stat = {
  name: string
  value: string
}

export const Card = ({
  timeLeft,
  cardImageSrc,
  coinName,
  coinTicker,
  logoUrl,
  descriptionText,
  stats,
  chainIcon,
  cmsData,
  isTimer,
  slug,
  isHardcoded,
}: CardProps) => {
  const [isLive, setIsLive] = useState(timeLeft === 'live')
  const timer = useCountdownTimer(
    cmsData?.privateSale?.start ?? new Date().toISOString(),
  )

  const { ref, shouldAnimate } = useShouldAnimate()

  useEffect(() => {
    if (isTimer && !isLive && timer === 'completed') {
      setIsLive(true)
    }
  }, [timer, isLive, isTimer])

  const Outer = ({ children }: { children: React.ReactNode }) =>
    isHardcoded ? (
      <>{children}</>
    ) : (
      <Link to={`/fundraise/${slug}`}>{children}</Link>
    )

  return (
    <Outer>
      <Container
        ref={ref}
        shouldAnimate={shouldAnimate}
        isLive={isLive}
        isTimer={!isLive && isTimer}
        isHardcoded={isHardcoded}
      >
        <OpeningLabel
          isLive={isLive}
          isTimer={!isLive && isTimer}
          isHardcoded={isHardcoded}
        >
          {isHardcoded && (
            <img src="/img/icons/clock.svg" alt="coming soon icon" />
          )}
          {isLive && <LiveLoaderCircle />}
          <span>{!isLive && isTimer ? timer : isLive ? 'live' : timeLeft}</span>
        </OpeningLabel>
        <ImageContainer>
          <BlurImage src={cardImageSrc} />
          <CardImage src={cardImageSrc} />
        </ImageContainer>
        <div className="px-4">
          <Box dc g={0} marginBottom={8}>
            <CoinInfoTag marginTop="20px">
              {!isHardcoded && <CoinLogo src={logoUrl} />}
              <CoinTagDetails>
                <CoinName>{coinName}</CoinName>
                <CoinTicker>{coinTicker}</CoinTicker>
              </CoinTagDetails>
              <ChainIconWrapper>
                <ChainIcon isSmall src={chainIcon} />
              </ChainIconWrapper>
            </CoinInfoTag>
          </Box>
          <DescriptionText>
            <Dotdotdot clamp={3}>{removeMd(descriptionText)}</Dotdotdot>
          </DescriptionText>
          <Box dc g={0} marginTop={16}>
            {stats.map(({ name, value }) => (
              <StatWrapper key={name}>
                <StatName>{name}</StatName>
                <StatValue>{value}</StatValue>
              </StatWrapper>
            ))}
          </Box>
        </div>
      </Container>
    </Outer>
  )
}
